import React, { useContext, useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";
import { Dropdown } from "react-bootstrap";
import classNames from "classnames";

// Components
import BorderSpinner from "../../components/Spinners/BorderSpinner";
import ModalAssign from "../../components/modals/ModalAssign";
import {
  ChatState,
  TypeMsg,
  formatLastMessageTime,
  MessageStatus,
  NetworkIcon,
} from "../../components/chat/ChatComponents";

// Context
import { ChatContext } from "../../context/chat/ChatContext";
import { SocketContext } from "../../context/SocketContext";
import { AuthContext } from "../../context/AuthContext";

// Utils
import createPermsObj from "../../utils/permissionsHelper";
import useInfinityScroll from "../../hooks/useInfinityScroll";
import { fetchWithToken } from "../../api/fetch";
import { types } from "../../types/types";
import { toast } from "react-toastify";
import toastConfig from "../../utils/toastConfig";
const baseURL = process.env.REACT_APP_API;

/**
 * Componente funcional que renderiza la lista de chats
 *
 * @param {Object} props Propiedades del componente
 * @param {string} props.filter Filtro de los chats
 * @param {Object[]} props.searchResults Resultados de la búsqueda
 * @param {boolean} props.isSearching Indica si se está buscando
 * @returns {JSX.Element} Componente de react
 */
const ChatList = ({ filter, searchResults, isSearching }) => {
  const [showModal, setShowModal] = useState(false); // Indica si se muestra el modal de asignación
  const [isLoading, setIsLoading] = useState(true); // Indica si se están cargando chats
  const [hasMoreChats, setHasMoreChats] = useState(true); // Indica si hay más chats por cargar
  const [selectedUserId, setSelectedUserId] = useState(null); // Id del usuario seleccionado
  const [scrollPosition, setScrollPosition] = useState(0); // Posición del scroll
  const simpleBarRef = useRef(null); // Referencia al contenedor de scroll

  // Custom hook para el scroll infinito
  const { loadMoreRef, offset, setOffset } = useInfinityScroll(
    isLoading,
    isSearching
  );

  // Contexto del chat
  const { chatState, dispatch } = useContext(ChatContext);
  const { socket } = useContext(SocketContext);
  const { activeChat, chatList } = chatState;

  // Contexto de autenticación
  const { auth } = useContext(AuthContext);
  const { uid } = auth;

  // Permisos de la sección de Chats
  const chatPerms = createPermsObj(auth.permissions, "Gestión de Chats");

  // Mapeo de los filtros
  const filterMap = {
    "Mis Chats": uid,
    "No leídos": "unread",
    Etiquetados: "tag",
    Pagos: "pagos",
  };

  // Información para la asignación de etiquetas
  const info = {
    type: "etiqueta",
    id_user: selectedUserId,
  };

  // Valor del filtro
  const filterValue = filterMap[filter] || "Todos";

  // Cargar los chats de la búsqueda
  useEffect(() => {
    if (isSearching & (searchResults.length > 0)) {
      dispatch({ type: types.loadChats, payload: searchResults });
      setHasMoreChats(false);
      setIsLoading(false);
    }
  }, [searchResults, isSearching, dispatch]);

  // Carga inicial de chats
  useEffect(() => {
    const loadInitialChats = async () => {
      if (isSearching) return;
      const resp = await fetchWithToken(`sms/0/${filterValue}`);
      if (resp.ok) {
        const users = resp.data.users;
        dispatch({ type: types.loadChats, payload: users });
        setHasMoreChats(resp.data.total > 15 ? true : false);
      }
      setIsLoading(false);
    };

    if (!isSearching) {
      // Limpiar la lista de chats al cambiar el filtro
      dispatch({ type: types.clearChatList });
      setOffset(0);
      setIsLoading(true);
      setHasMoreChats(true);

      if (simpleBarRef.current) {
        const scrollableElement = simpleBarRef.current.getScrollElement();
        scrollableElement.scrollTop = 0;

        // Cargar los chats iniciales
        loadInitialChats();
      }
    }
  }, [filterValue, dispatch, setOffset, isSearching]);

  // Carga más chats al hacer scroll
  useEffect(() => {
    if (!hasMoreChats || offset === 0) return;
    setIsLoading(true);

    if (simpleBarRef.current) {
      const scrollableElement = simpleBarRef.current.getScrollElement();
      setScrollPosition(scrollableElement.scrollTop);
    }

    const loadMoreChats = async () => {
      const resp = await fetchWithToken(`sms/${offset}/${filterValue}`);
      if (resp.ok) {
        const users = resp.data.users;
        dispatch({ type: types.loadMoreChats, payload: users });
      }
      setIsLoading(false);
    };
    loadMoreChats();
  }, [offset, filterValue, dispatch, setIsLoading, hasMoreChats]);

  // Limpiar la lista de chats al cambiar el filtro
  useEffect(() => {
    dispatch({ type: types.clearChatList });
    setOffset(0);
    setIsLoading(true);
    setScrollPosition(0);
  }, [filter, dispatch, setOffset]);

  // Mantener la posición del scroll al cargar más chats
  useEffect(() => {
    if (simpleBarRef.current && scrollPosition !== 0 && isLoading) {
      const scrollableElement = simpleBarRef.current.getScrollElement();
      scrollableElement.scrollTop = scrollPosition;
    }
  }, [scrollPosition, isLoading]);

  // Scroll al inicio de la lista al buscar
  useEffect(() => {
    if (isSearching && simpleBarRef.current) {
      const scrollableElement = simpleBarRef.current.getScrollElement();
      scrollableElement.scrollTop = 0;
    }
  }, [isSearching]);

  // Actualización del Chat List en tiempo real
  useEffect(() => {
    // Actualizar la lista de chats cuando se recibe un nuevo mensaje
    const handleNewMessage = (newUser) => {
      if (isSearching) return; // No actualizar si se está buscando
      dispatch({ type: types.newMessageOnChat, payload: newUser });
    };

    // Actualizar la etiqueta de un chat
    const handleTagChange = (data) => {
      dispatch({ type: types.tagChange, payload: data });
    };

    // Actualizar la lista de chats
    const handleChatList = (data) => {
      dispatch({ type: types.refreshChatList, payload: data });
    };

    socket?.on("new-sms", handleNewMessage);
    socket?.on("mark-us", handleTagChange);
    socket?.on("updChatList", handleChatList);

    return () => {
      socket?.off("new-sms", handleNewMessage);
      socket?.off("mark-us", handleTagChange);

      socket?.off("updChatList", handleChatList);
    };
  }, [dispatch, socket, activeChat, isSearching]);

  // Actualizar el estado de los mensajes
  useEffect(() => {
    const handleNewMessage = (newUser) => {
      dispatch({
        type: types.refreshChatList,
        payload: newUser,
      });
    };

    socket?.on("us-item", handleNewMessage);

    return () => socket?.off("us-item", handleNewMessage);
  }, [socket, dispatch, activeChat, chatList]);
  return (
    <>
      <SimpleBar
        className="px-3"
        style={{
          height: "calc(100vh - 290px)",
          width: "100%",
          overflowY: "auto",
        }}
        ref={simpleBarRef}
      >
        {chatList.length === 0 && !hasMoreChats ? (
          <div className="d-flex justify-content-center align-items-center">
            <h4 className="text-muted">¡Ops! No hay chats</h4>
          </div>
        ) : (
          chatList.map((user, key) => (
            <div
              key={key}
              className={classNames("cursor-pointer d-flex border-bottom p-2", {
                "bg-light": user.id === activeChat?.id,
              })}
              onClick={async () => {
                if (user.id === activeChat?.id) return;
                if (user.smsunread > 0) socket?.emit("leidos", user.id);

                dispatch({
                  type: types.selectChat,
                  payload: user,
                });
              }}
            >
              <NetworkIcon network={user?.red} />

              <img
                src={
                  user.foto.includes("http")
                    ? `${user.foto}`
                    : `${baseURL}/${user.foto}`
                }
                className="me-2 rounded-circle border"
                height="48"
                alt="Foto de perfil"
                style={{
                  minWidth: "48px",
                }}
              />

              <div
                style={{
                  minWidth: "calc(100% - 55px)",
                }}
              >
                <div>
                  <ChatState user={user} />

                  <p className="font-13 my-0 ms-1 d-flex align-items-center">
                    {`${user.first_name ?? ""} ${user.last_name ?? ""}`}{" "}
                    <span
                      className="text-info ms-2"
                      style={{
                        display: "inline-block",
                        maxWidth: "150px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={user.customer} // opcional: para que muestre el nombre completo al pasar el mouse
                    >
                      {user.customer}
                    </span>
                  </p>

                  <div className="d-flex align-items-center float-end">
                    <span className="text-secondary-lighten font-11 me-1">
                      {formatLastMessageTime(user.date)}
                    </span>

                    {user.mark && (
                      <span
                        className="badge font-10 text-white"
                        style={{ backgroundColor: user.color_mk }}
                        title={user.mark}
                      >
                        <i className="mdi mdi-bookmark font-12" />
                      </span>
                    )}

                    {user.pago ? (
                      <span
                        className="badge font-10 text-white bg-warning mx-1"
                        title={"PAGO"}
                      >
                        <i className="mdi mdi-cash font-12" />
                      </span>
                    ) : null}

                    <Dropdown
                      className="conversation-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Dropdown.Toggle
                        variant="link"
                        className="btn btn-sm btn-link arrow-none shadow-none p-0"
                      >
                        <i className="uil uil-ellipsis-v text-secondary" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {user.idmark > 0 ? (
                          <Dropdown.Item
                            className="text-danger fw-bold conversation-action"
                            onClick={async () => {
                              const resp = await fetchWithToken(
                                "etiquetas/unassign-mark",
                                { id_us: user.id_user },
                                "PUT"
                              );
                              // Muestra mensaje de éxito o error
                              if (resp.ok) {
                                toast.success(resp.msg, toastConfig);
                                socket.emit("addMark", {
                                  id_us: user.id_user,
                                });
                              } else {
                                toast.error(resp.msg, toastConfig);
                              }
                            }}
                          >
                            Quitar etiqueta
                          </Dropdown.Item>
                        ) : null}

                        <Dropdown.Item
                          onClick={() => {
                            setSelectedUserId(user.id_user);
                            setShowModal(true);
                          }}
                          className="conversation-action"
                        >
                          Asignar etiqueta
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() => {
                            socket?.emit("unread", user.id_user);
                          }}
                        >
                          Marcar como no leído
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() => {
                            dispatch({
                              type: types.closeChat,
                              payload: user.id_user,
                            });
                          }}
                        >
                          Cerrar chat
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            socket?.emit("pago", {
                              id: user.id_user,
                              pago: user.pago ? false : true,
                            });
                          }}
                        >
                          {user.pago ? "Desmarcar pago" : "Marcar como pago"}
                        </Dropdown.Item>
                        {user.idcus && (
                          <Dropdown.Item
                            onClick={() => {
                              socket?.emit("desasociar", user.id_user);
                              dispatch({
                                type: types.closeChat,
                                payload: user.id_user,
                              });
                            }}
                          >
                            Cancelar vínculo con cliente
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center text-secondary-lighten mt-2">
                  <span
                    className="font-14 me-3"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={user.object}
                  >
                    {user.status === "send" && (
                      <span className="text-secondary font-14 fw-bold me-1">
                        Tú:
                      </span>
                    )}
                    <TypeMsg chatItem={user} />
                  </span>

                  <div className="d-flex align-items-center gap-1 font-16">
                    {user.smsunread > 0 ? (
                      <span
                        className="bg-success rounded-circle text-white fw-bold font-12 px-1"
                        title="No leído"
                      >
                        <span>{user.smsunread}</span>
                      </span>
                    ) : user.status === "send" ? (
                      <MessageStatus status={user.statesms} />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        {hasMoreChats && <BorderSpinner />}

        {/* Referencia para el scroll infinito */}
        <div ref={loadMoreRef} style={{ height: "20px", width: "100%" }} />
      </SimpleBar>
      {showModal ? (
        <ModalAssign
          show={showModal}
          handleClose={() => {
            setShowModal(false);
            setSelectedUserId(null);
          }}
          info={info}
        />
      ) : null}
    </>
  );
};

export default ChatList;
